import React from "react"
import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"
import moment from "moment"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { useNavigate, useParams } from "react-router-dom"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_KPI_LIST_OPTIONS_FOR_SELECT2, IS_DEV_ENV_ENABLED } from "@common-constants/*"
import MyAlert from "@common-utils/MyAlert"
import MyModal from "@common-utils/MyModal"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MyDateDayMonthYearFormField from "@common-components/fields/MyDateDayMonthYearFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import CenteredLoader from "./_CenteredLoader"
import PermissionsSelectField, { NestedPermission } from "../create/PermissionsSelectField"
import { PERMISSIONS_DATA } from "../create/permissionsData"
import { IPersonnelDEFinalizeRequestModel, IPersonnelDemandeEmbaucheModel } from "../core/_models"
import { personnelDEGetDetailsRequest, personnelDEFinaliserRequest } from "../core/_requests"
import { CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT, CONST_API_UTILS_GET_AVAILABLE_NFC_ENDPOINT } from "../../../endpoints"
import { ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE } from "../../gestion/routing/routing-endpoints"

// Sans accents (é -> e, ç -> c…) + minuscules + espaces -> tirets : les adresses email ne contiennent pas d'accents.
const toEmailPart = (s?: string): string =>
     (s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/\s+/g, "-")

const escapeHtml = (s: string) =>
     String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string))

// Aplati l'arbre des permissions en une table valeur -> libellé, pour afficher les droits choisis dans le récapitulatif.
const flattenPermissionLabels = (nodes: NestedPermission[], map: Record<string, string> = {}): Record<string, string> => {
     nodes.forEach(n => {
          map[String(n.value)] = n.label
          if (n.children) flattenPermissionLabels(n.children, map)
     })
     return map
}
const PERMISSION_LABELS = flattenPermissionLabels(PERMISSIONS_DATA)

const FinaliserDemandeEmbauche = () => {
     const navigate = useNavigate()
     const { id } = useParams<{ id: string }>()
     const demandeId = Number(id)

     const [demande, setDemande] = React.useState<IPersonnelDemandeEmbaucheModel | null>(null)
     const [loading, setLoading] = React.useState<boolean>(true)
     // Récapitulatif de confirmation : valeurs en attente de validation (modale ouverte si non nul) + état d'envoi.
     const [pending, setPending] = React.useState<IPersonnelDEFinalizeRequestModel | null>(null)
     const [submitting, setSubmitting] = React.useState<boolean>(false)
     const helpersRef = React.useRef<FormikHelpers<IPersonnelDEFinalizeRequestModel> | null>(null)

     React.useEffect(() => {
          personnelDEGetDetailsRequest(demandeId)
               .then(r => setDemande(r.data))
               .catch(() => toast.error("Impossible de charger la demande."))
               .finally(() => setLoading(false))
     }, [demandeId])

     if (loading) return <CenteredLoader />
     if (!demande) return <MyAlert type={"danger"} isDismissible={false}><>Demande introuvable.</></MyAlert>

     const p = demande.personnel
     const emailPro = (p as any)?.mailProfessionnel ?? ""
     const groupTeamDefault = `${toEmailPart(p?.prenom)}.${toEmailPart(p?.nom)}.team@cts-corporate.com`
     const groupRespDefault = `${toEmailPart(p?.prenom)}.${toEmailPart(p?.nom)}.responsables@cts-corporate.com`

     const schema = Yup.object().shape({
          permissions: Yup.array().min(1, "Sélectionnez au moins une permission").label(`"Permissions"`).required(),
          authorizedKpi: Yup.array(),
          nfc: Yup.number().nullable(),
          team: Yup.array(),
          dateActivationCompte: Yup.string().label(`"Date d'activation"`).required(),
          password: Yup.string().test("pw", "Au moins 6 caractères", v => !v || v.length >= 6),
          createGoogleAccount: Yup.boolean(),
          gwsGroupMailTeam: Yup.string().email().when("createGoogleAccount", { is: true, then: s => s.required() }),
          gwsGroupMailResponsables: Yup.string().email().when("createGoogleAccount", { is: true, then: s => s.required() }),
     })

     const initialValues: IPersonnelDEFinalizeRequestModel = {
          permissions: [],
          authorizedKpi: [],
          dateActivationCompte: moment().format("YYYY-MM-DD"),
          createGoogleAccount: false,
          gwsGroupMailTeam: groupTeamDefault,
          gwsGroupMailResponsables: groupRespDefault,
     }

     // Soumission du formulaire : on ouvre la modale de récapitulatif (la finalisation réelle se fait à la confirmation).
     function handleSubmit(values: IPersonnelDEFinalizeRequestModel, helpers: FormikHelpers<IPersonnelDEFinalizeRequestModel>) {
          helpersRef.current = helpers
          setPending(values)
          helpers.setSubmitting(false) // la modale prend le relais (loader porté par le bouton de confirmation)
     }

     // Confirmation depuis la modale : provisionnement + activation.
     function runFinalize() {
          if (!pending) return
          const values = pending
          const helpers = helpersRef.current
          setSubmitting(true)
          personnelDEFinaliserRequest(demandeId, values)
               .then(async r => {
                    const warnings: string[] = r?.data?.warnings ?? []
                    const gp: any = r?.data?.googleProvisioning ?? null
                    if (gp) {
                         const rows: string[] = [`<li class="mb-2">✅ <b>Compte Google</b><br/><span class="text-muted">${escapeHtml(gp.account)}</span></li>`]
                         ;(gp.groups ?? []).forEach((g: { name: string; email: string }) =>
                              rows.push(`<li class="mb-2">✅ <b>Groupe Google</b> : ${escapeHtml(g.name)}<br/><span class="text-muted">${escapeHtml(g.email)}</span></li>`)
                         )
                         rows.push(
                              gp.sharedDrive?.created
                                   ? `<li class="mb-2">✅ <b>Drive partagé</b> : ${escapeHtml(gp.sharedDrive.name)}</li>`
                                   : `<li class="mb-2">➖ <b>Drive partagé</b> <span class="text-muted">(non créé)</span></li>`
                         )
                         rows.push(gp.signature?.created ? `<li class="mb-2">✅ <b>Signature Gmail</b></li>` : `<li class="mb-2">➖ <b>Signature Gmail</b> <span class="text-muted">(non créée)</span></li>`)
                         if (gp.credentialsEmailSentTo)
                              rows.push(`<li class="mb-2">✅ <b>Email des identifiants</b> envoyé à<br/><span class="text-muted">${escapeHtml(gp.credentialsEmailSentTo)}</span></li>`)
                         const warnHtml = warnings.length
                              ? `<div class="alert alert-warning text-start mt-4 mb-0"><b>Avertissements</b><ul class="mb-0 mt-2 ps-3">${warnings.map(w => `<li>${escapeHtml(w)}</li>`).join("")}</ul></div>`
                              : ""
                         await Swal.fire({
                              icon: "success",
                              title: "Embauche finalisée",
                              html: `<div class="text-start"><p class="mb-3">Éléments provisionnés :</p><ul class="list-unstyled mb-0">${rows.join("")}</ul>${warnHtml}</div>`,
                              confirmButtonText: "OK",
                              width: 600,
                         })
                    } else {
                         toast.success("Embauche finalisée. Le compte a été activé.")
                         warnings.forEach(w => toast.warning(w, { autoClose: false }))
                    }
                    navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data as IHttpErrorResponseModel
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers?.setFieldError(key, error.errors[key])
                    }
                    toast.error(error?.detail ?? "Une erreur est survenue.", { autoClose: false })
               })
               // Ferme la modale (succès → redirection ; erreur → l'utilisateur revoit le formulaire et ses erreurs).
               .finally(() => {
                    setSubmitting(false)
                    setPending(null)
               })
     }

     return (
          <>
          <div className="card">
               <div className="card-header">
                    <h3 className="card-title">
                         Finaliser l'embauche — {p?.prenom} {p?.nom}
                    </h3>
               </div>
               <div className="card-body">
                    <MyAlert type={"primary"} classNames={"mb-6"} isDismissible={false}>
                         <>Dernière étape : droits applicatifs, carte NFC, co-équipiers et (optionnellement) compte Google. La validation active le compte du collaborateur.</>
                    </MyAlert>

                    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                         {helpers => (
                              <Form noValidate autoComplete="off">
                                   <div className="row">
                                        <div className="col-md-6">
                                             {/* Structure alignée sur MySelectFormField (label fs-6 + champ mt-1) pour aligner les deux selects. */}
                                             <div className="row mb-6">
                                                  <div className="col-12 d-flex align-items-center">
                                                       <label className="fs-6 fw-bolder text-gray-900">Droits & KPI <span className="text-danger">*</span></label>
                                                  </div>
                                                  <div className="col-12 mt-1">
                                                       <PermissionsSelectField name={"permissions"} options={PERMISSIONS_DATA} formikProps={helpers} />
                                                       {helpers.errors.permissions && helpers.touched.permissions && (
                                                            <div className="text-danger mt-2">{helpers.errors.permissions as string}</div>
                                                       )}
                                                  </div>
                                             </div>
                                        </div>
                                        <div className="col-md-6">
                                             <MySelectFormField
                                                  name={"authorizedKpi"}
                                                  label={"KPI autorisés"}
                                                  formikProps={helpers}
                                                  validationSchema={schema}
                                                  classNameLayout={"mb-6"}
                                                  placeholder={"KPI"}
                                                  isMulti
                                                  options={CONST_KPI_LIST_OPTIONS_FOR_SELECT2}
                                             />
                                        </div>
                                   </div>

                                   <div className="separator separator-dashed mb-6" />
                                   <label className="fs-5 fw-bolder text-gray-900 mb-3 d-block">Carte NFC & co-équipiers</label>
                                   <div className="row">
                                        <div className="col-md-6">
                                             <MySelectGetAllDataFromServerFormField
                                                  name={"nfc"}
                                                  label={"Carte NFC"}
                                                  formikProps={helpers}
                                                  method={"GET"}
                                                  endpoint={CONST_API_UTILS_GET_AVAILABLE_NFC_ENDPOINT}
                                                  classNameLayout={"mb-6"}
                                                  placeholder={"Choisissez une carte"}
                                             />
                                        </div>
                                        <div className="col-md-6">
                                             <MySelectGetAllDataFromServerFormField
                                                  name={"team"}
                                                  label={"Co-équipiers"}
                                                  formikProps={helpers}
                                                  method={"GET"}
                                                  endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                                  classNameLayout={"mb-6"}
                                                  placeholder={"Co-équipiers"}
                                                  isMulti
                                             />
                                        </div>
                                   </div>

                                   <div className="separator separator-dashed mb-6" />
                                   <label className="fs-5 fw-bolder text-gray-900 mb-3 d-block">Activation & compte Google</label>
                                   <div className="row">
                                        <div className="col-md-6">
                                             <MyDateDayMonthYearFormField name={"dateActivationCompte"} label={"Date d'activation du compte"} formikProps={helpers} validationSchema={schema} classNameLayout={"mb-6"} placeholder={"Date d'activation"} />
                                        </div>
                                        <div className="col-md-6">
                                             <MyInputFormField name={"password"} label={"Mot de passe applicatif (optionnel)"} formikProps={helpers} type={"password"} classNameLayout={"mb-6"} placeholder={"Laissez vide pour générer un mot de passe temporaire"} />
                                        </div>
                                   </div>
                                   <MyCheckboxFormField name={"createGoogleAccount"} label={"Compte Google"} text={"Créer un compte Google Workspace"} formikProps={helpers} classNameLayout={"mb-6"} />

                                   {helpers.values.createGoogleAccount && (
                                        <>
                                             {IS_DEV_ENV_ENABLED && (
                                                  <MyAlert type={"warning"} classNames={"mb-4"} isDismissible={false}>
                                                       <>
                                                            <strong>⚠️ Développement.</strong> Un vrai compte Google et de vrais groupes seront créés sur le Workspace. Une confirmation sera demandée.
                                                       </>
                                                  </MyAlert>
                                             )}
                                             <MyAlert type={"primary"} classNames={"mb-6"} isDismissible={false}>
                                                  <>
                                                       Un compte Google sera créé pour <strong>{emailPro || `${p?.prenom} ${p?.nom}`}</strong>, avec deux groupes GWS et un mot de passe temporaire envoyé au responsable.
                                                  </>
                                             </MyAlert>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <MyInputFormField name={"gwsGroupMailTeam"} label={"Groupe GWS Team"} formikProps={helpers} validationSchema={schema} classNameLayout={"mb-6"} placeholder={"Groupe GWS Team"} />
                                                  </div>
                                                  <div className="col-md-6">
                                                       <MyInputFormField name={"gwsGroupMailResponsables"} label={"Groupe GWS Resp."} formikProps={helpers} validationSchema={schema} classNameLayout={"mb-6"} placeholder={"Groupe GWS Resp."} />
                                                  </div>
                                             </div>
                                        </>
                                   )}

                                   <div className="separator bg-primary my-5" />
                                   <div className="d-flex justify-content-between">
                                        <button type="button" className="btn btn-sm btn-light" onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE)}>
                                             Annuler
                                        </button>
                                        <button type="submit" className="btn btn-sm btn-success d-inline-flex align-items-center" disabled={helpers.isSubmitting}>
                                             {helpers.isSubmitting ? <MySimpleSpinner size={"sm"} /> : <>Finaliser l'embauche <i className="fas fa-check ms-1" /></>}
                                        </button>
                                   </div>
                              </Form>
                         )}
                    </Formik>
               </div>
          </div>

          {/* Récapitulatif de confirmation (modale plus large qu'un swal, pas de scroll). */}
          {pending &&
               (() => {
                    const v = pending
                    const permLabels = (v.permissions ?? []).map(x => PERMISSION_LABELS[String(x)] ?? String(x))
                    const kpiLabels = (v.authorizedKpi ?? []).map(x => CONST_KPI_LIST_OPTIONS_FOR_SELECT2.find(o => String(o.value) === String(x))?.label ?? String(x))
                    const driveName = `${p?.prenom ?? ""} ${(p?.nom ?? "").toUpperCase()}`.trim()
                    const showDevWarn = !!v.createGoogleAccount && IS_DEV_ENV_ENABLED
                    const labelList = (arr: string[]) =>
                         arr.length ? (
                              <ul className="mb-0 mt-1 ps-4">
                                   {arr.map((x, i) => (
                                        <li key={i}>{x}</li>
                                   ))}
                              </ul>
                         ) : (
                              <span className="text-muted ms-2">Aucun</span>
                         )
                    return (
                         <MyModal title={"Confirmer la finalisation"} show={true} onCloseCallback={() => !submitting && setPending(null)} size={"lg"}>
                              <div className="fs-6">
                                   {/* Avertissement développement en tête */}
                                   {showDevWarn && (
                                        <MyAlert type={"danger"} classNames={"mb-4"} isDismissible={false}>
                                             <>
                                                  <strong>⚠️ Développement.</strong> Un <strong>vrai compte Google</strong> et de <strong>vrais groupes</strong> seront créés sur le Workspace.
                                             </>
                                        </MyAlert>
                                   )}

                                   <div className="mb-3">
                                        <span className="fw-bold">Droits applicatifs</span>
                                        {labelList(permLabels)}
                                   </div>
                                   <div className="mb-3">
                                        <span className="fw-bold">KPI autorisés</span>
                                        {labelList(kpiLabels)}
                                   </div>
                                   <div className="mb-3">
                                        <span className="fw-bold">Carte NFC :</span> {v.nfc ? <b>Sélectionnée</b> : <span className="text-muted">Aucune</span>}
                                   </div>
                                   <div className="mb-3">
                                        <span className="fw-bold">Co-équipiers :</span> {v.team?.length ?? 0} sélectionné(s)
                                   </div>
                                   <div className="mb-3">
                                        <span className="fw-bold">Date d'activation :</span> <b>{moment(v.dateActivationCompte).format("DD/MM/YYYY")}</b>
                                   </div>
                                   <div className="mb-3">
                                        <span className="fw-bold">Mot de passe applicatif :</span>{" "}
                                        {v.password ? <b>Personnalisé</b> : <span className="text-muted">Généré automatiquement</span>}
                                   </div>

                                   <div className="separator separator-dashed my-4" />

                                   {v.createGoogleAccount ? (
                                        <div className="alert alert-light-primary p-4 mb-0">
                                             <div className="fw-bold mb-2">✅ Compte Google : Oui</div>
                                             <div>
                                                  <span className="text-muted">Groupe Team :</span> <b>{v.gwsGroupMailTeam}</b>
                                             </div>
                                             <div>
                                                  <span className="text-muted">Groupe Resp. :</span> <b>{v.gwsGroupMailResponsables}</b>
                                             </div>
                                             <div className="mt-2">
                                                  Un <b>Drive partagé</b> sera créé sur le compte <b>agora@cts-corporate.com</b> sous le nom «&nbsp;<b>{driveName}</b>&nbsp;».
                                             </div>
                                        </div>
                                   ) : (
                                        <div className="text-muted">
                                             ➖ <b>Compte Google : Non</b>
                                        </div>
                                   )}

                                   <div className="d-flex justify-content-end gap-2 mt-8">
                                        <button type="button" className="btn btn-sm btn-light" onClick={() => setPending(null)} disabled={submitting}>
                                             Revoir
                                        </button>
                                        <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center" onClick={runFinalize} disabled={submitting}>
                                             {submitting ? <MySimpleSpinner size={"sm"} /> : <>Confirmer et finaliser <i className="fas fa-check ms-1" /></>}
                                        </button>
                                   </div>
                              </div>
                         </MyModal>
                    )
               })()}
          </>
     )
}

export default FinaliserDemandeEmbauche
